import React, { useEffect, useRef } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags, useRequestData } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import { ErrorBoundary } from 'wsm-error-boundary';
import ErrorAlert from '../components/common/ErrorAlert';
import CompareTable from '../components/CompareTable';
import useScroll from '../hooks/useScroll';
import '../global-sass/style.scss';
import StickyCardContainer from '../components/StickyCardContainer';
import CompareHeader from '../components/CompareHeader';
import { setVehicles } from '../features/vehicles';
import fetchVehiclesByUuids from '../utilities/fetchVehiclesByUuids';
import { UUID_REGEX } from '../global-constants';

const WidgetContainer = () => {
	const { onScroll, compareItemWidth } = useScroll();
	const dispatch = useDispatch();
	const vehicles = useSelector((state) => state.vehicles);
	// `useRequestData()` reads from the framework's `state.requestData` slice
	// (provided by ws-scripts) which exposes locale + deviceType but not
	// siteId / pageAlias / pageId — those come from our SSR-populated
	// `moreRequestData` slice instead.
	const { locale, deviceType } = useRequestData();
	const { siteId, pageAlias, pageId } = useSelector(
		(state) => state.moreRequestData
	);
	const flags = useFlags();

	const vehiclesRef = useRef(vehicles);
	useEffect(() => {
		vehiclesRef.current = vehicles;
	}, [vehicles]);

	// AC5: Supplement sessionStorage UUIDs into Redux on client hydration
	useEffect(() => {
		if (!isBrowser) {
			return undefined;
		}
		let cancelled = false;
		const fetchAndMerge = async () => {
			try {
				const stored = sessionStorage.getItem(
					'DDC.inventory-compare.selected'
				);
				if (!stored) {
					return;
				}
				const storedUuids = [
					...new Set(
						stored
							.split(',')
							.filter((uuid) => UUID_REGEX.test(uuid))
					)
				];
				if (storedUuids.length === 0) {
					return;
				}
				const currentVehicles = vehiclesRef.current;
				const existingUuids = currentVehicles.map((v) => v.uuid);
				const missingUuids = storedUuids.filter(
					(uuid) => !existingUuids.includes(uuid)
				);
				if (missingUuids.length === 0) {
					return;
				}
				const fetched = await fetchVehiclesByUuids({
					uuids: missingUuids,
					siteId,
					locale,
					deviceType,
					pageAlias,
					pageId,
					flags
				});
				if (!cancelled && fetched.length > 0) {
					dispatch(setVehicles([...vehiclesRef.current, ...fetched]));
				}
			} catch (e) {
				// fail silently
			}
		};
		fetchAndMerge();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only hydration; deps are stable per session
	}, []);

	return (
		<ErrorBoundary
			newRelicPageAction="WS COMPARE VEHICLE ERROR BOUNDARY"
			errorMarkup={<ErrorAlert />}
			errorHandler={(error, errorInfo) => {
				const newError = new Error(
					`Encountered an error in ws-compare-vehicle.\n${error}`
				);
				newError.originalError = error;
				newError.originalStackTrace = errorInfo.componentStack;
				logger.error(`${newError}\n${newError.originalStackTrace}`);
			}}
		>
			<>
				<StickyCardContainer compareItemWidth={compareItemWidth} />
				<CompareHeader />
				<div
					onScroll={onScroll}
					className="compare-item-list d-block overflow-auto pr-4 pr-lg-6 pr-xl-8 pl-xl-4"
				>
					<CompareTable />
				</div>
			</>
		</ErrorBoundary>
	);
};

WidgetContainer.propTypes = {};

export default WidgetContainer;
